import { THEMES, useTheme } from "@elit-hair/elit-ui";
import { useEffect, useRef } from "react";

import {
    DARWIN_AVATAR_AUTO_SLEEP_MS,
    DARWIN_AVATAR_COLOR,
    DARWIN_AVATAR_EYE_COLOR,
    DARWIN_AVATAR_SIZE,
} from "@/constants/ai-chat";
import { cn } from "@/lib/utils";
import { DarwinAvatarProps, DarwinAvatarState } from "@/types/darwin-avatar.types";
import AgentRobotAvatar from "@/vendor/agent-robot-avatar";

const EYE_ELEMENT_IDS = ["leftBase", "rightBase", "leftInputBase", "rightInputBase"] as const;

function getAvatarFromContainer(container: HTMLDivElement | null): AgentRobotAvatar | null {
    const element = container?.firstElementChild;
    if (element instanceof AgentRobotAvatar) {
        return element;
    }

    return null;
}

function applyAvatarState(avatar: AgentRobotAvatar, state: DarwinAvatarState) {
    if (state === "waiting") {
        avatar.setAntennaFlash(true);
        void avatar.startWaiting();
        return;
    }

    avatar.setAntennaFlash(false);
    avatar.stopWaiting();
    void avatar.play(state);
}

function applyAvatarColors(avatar: AgentRobotAvatar, bodyColor: string, eyeColor: string) {
    avatar.setAttribute("color", bodyColor);

    const shadowRoot = avatar.shadowRoot;
    if (!shadowRoot) {
        return;
    }

    for (const id of EYE_ELEMENT_IDS) {
        const eyePart = shadowRoot.getElementById(id);
        if (eyePart) {
            eyePart.setAttribute("fill", eyeColor);
        }
    }
}

export function DarwinAvatar({
    size = DARWIN_AVATAR_SIZE.HEADER,
    state = "idle",
    pointerFollow = true,
    className,
}: DarwinAvatarProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const { theme } = useTheme();
    const isDark = theme === THEMES.DARK;
    const bodyColor = isDark ? DARWIN_AVATAR_COLOR.DARK : DARWIN_AVATAR_COLOR.LIGHT;
    const eyeColor = isDark ? DARWIN_AVATAR_EYE_COLOR.DARK : DARWIN_AVATAR_EYE_COLOR.LIGHT;
    const bodyColorRef = useRef(bodyColor);
    const eyeColorRef = useRef(eyeColor);
    const stateRef = useRef(state);
    bodyColorRef.current = bodyColor;
    eyeColorRef.current = eyeColor;
    stateRef.current = state;

    useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        const avatar = new AgentRobotAvatar();
        avatar.setAttribute("size", String(size));
        avatar.setAttribute("auto-sleep", DARWIN_AVATAR_AUTO_SLEEP_MS);
        avatar.setAttribute("motion", "auto");
        avatar.setPointerFollow(pointerFollow);
        container.replaceChildren(avatar);
        applyAvatarColors(avatar, bodyColorRef.current, eyeColorRef.current);
        applyAvatarState(avatar, stateRef.current);

        return () => {
            container.replaceChildren();
        };
    }, [pointerFollow, size]);

    useEffect(() => {
        const avatar = getAvatarFromContainer(containerRef.current);
        if (!avatar) {
            return;
        }

        applyAvatarColors(avatar, bodyColor, eyeColor);
    }, [bodyColor, eyeColor]);

    useEffect(() => {
        const avatar = getAvatarFromContainer(containerRef.current);
        if (!avatar) {
            return;
        }

        applyAvatarState(avatar, state);
    }, [state]);

    return (
        <div
            ref={containerRef}
            className={cn("shrink-0 overflow-visible", className)}
            style={{ width: size, height: size }}
        />
    );
}
